import React from 'react';
import { Badge, Alert } from 'react-bootstrap';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { getQuizFullDisplayCaption } from '../utils/quizDisplayTitle';
import { isPwaStandalone } from '../utils/pwaStandalone';
import PwaInstallButton from './PwaInstallButton';
import PvpResultParticipantXpBlock from './PvpResultParticipantXpBlock';
import './QuizPlayer.css';

export interface PvpQuizResultsMappedParticipant {
  userId: string;
  nickname: string;
  rank: number;
  isHost: boolean;
  score: number;
  correctAnswers: number;
  totalQuestions: number;
  completionTime: number;
  earnedXP: number;
  newLevel: number;
  winnerBonusXp: number;
  isVip?: boolean;
  baseXp: number;
  bonusXpPerCorrectAnswer: number;
  totalBonusXp: number;
  soloVipXpBonusAmount: number;
  pvpArenaExtraXp: number;
  pvpAppXpBonusAmount?: number;
  pvpAppXpBonusPercent?: number | null;
  speedBonusXp?: number | null;
  soloXpPotionBonusAmount?: number;
  soloXpPotionBonusPercent?: number | null;
}

export interface PvpQuizResultsMapped {
  roomName: string;
  quizTitle: string;
  quizTitleEn?: string | null;
  quizTheme?: string | null;
  quizThemeEn?: string | null;
  quizFormat?: string;
  endedAt: string;
  results: PvpQuizResultsMappedParticipant[];
}

const PvpQuizResultsModalContent: React.FC<{ data: PvpQuizResultsMapped }> = ({ data }) => {
  const { t, i18n } = useSafeTranslation();
  const quizTitleDisplay = getQuizFullDisplayCaption(
    {
      title: data.quizTitle,
      titleEn: data.quizTitleEn,
      format: data.quizFormat,
      theme: data.quizTheme,
      themeEn: data.quizThemeEn,
    },
    i18n.language
  );
  const anyXp = data.results.some((p) => p.earnedXP > 0);
  const anyPvpAppBonus = data.results.some((p) => (p.pvpAppXpBonusAmount ?? 0) > 0);

  const formatCompletion = (seconds: number) =>
    `${Math.floor(seconds / 60)}:${Math.floor(seconds % 60).toString().padStart(2, '0')}`;

  return (
    <div className="text-center">
      <h2>{t('pvp.quizCompletedPvP', 'Quiz Completed!')}</h2>
      <p>
        {t('pvp.roomLabel', 'Room:')} <strong>{data.roomName}</strong>
      </p>
      <p>
        {t('pvp.quizLabel', 'Quiz:')} <strong>{quizTitleDisplay}</strong>
      </p>

      <div className="participants-ranking text-start mt-3">
        <h4 className="text-center">{t('pvp.finalRanking', 'Final Ranking')}</h4>
        {data.results.map((participant) => (
          <div
            key={participant.userId}
            className={`participant-result rank-${participant.rank} mb-3 p-2 border rounded`}
          >
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
              <div className="d-flex align-items-center flex-wrap">
                <Badge
                  bg={
                    participant.rank <= 0
                      ? 'secondary'
                      : participant.rank === 1
                        ? 'warning'
                        : participant.rank === 2
                          ? 'secondary'
                          : participant.rank === 3
                            ? 'danger'
                            : 'light'
                  }
                  className="me-2"
                >
                  {participant.rank <= 0
                    ? '—'
                    : participant.rank === 1
                      ? '🥇'
                      : participant.rank === 2
                        ? '🥈'
                        : participant.rank === 3
                          ? '🥉'
                          : `#${participant.rank}`}
                </Badge>
                <span className={`fw-bold${participant.isVip ? ' vip-subscriber-nickname' : ''}`}>
                  {participant.nickname}
                </span>
                {participant.isVip && (
                  <i className="bi bi-gem text-warning ms-1" title="VIP" aria-hidden />
                )}
                {participant.isHost && (
                  <Badge bg="warning" className="ms-2">
                    {t('pvp.host', 'Host')}
                  </Badge>
                )}
              </div>
              <div className="text-end">
                <div className="fw-bold">
                  {participant.score} {t('pvp.points', 'Points')}
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-center align-items-center flex-wrap gap-2 gap-sm-3 small text-muted mt-2 w-100">
              <span>
                {t('pvp.correctAnswersCount', '{{correct}}/{{total}} correct')
                  .replace('{{correct}}', String(participant.correctAnswers))
                  .replace('{{total}}', String(participant.totalQuestions))}
              </span>
              <span className="text-muted opacity-50" aria-hidden>
                ·
              </span>
              <span>
                {t('pvp.timeLabel', 'Time:')} {formatCompletion(participant.completionTime)}
              </span>
            </div>
            {participant.earnedXP > 0 && (
              <PvpResultParticipantXpBlock
                participant={{
                  earnedXP: participant.earnedXP,
                  baseXp: participant.baseXp,
                  bonusXpPerCorrectAnswer: participant.bonusXpPerCorrectAnswer,
                  totalBonusXp: participant.totalBonusXp,
                  correctAnswers: participant.correctAnswers,
                  soloVipXpBonusAmount: participant.soloVipXpBonusAmount,
                  pvpArenaExtraXp: participant.pvpArenaExtraXp,
                  winnerBonusXp: participant.winnerBonusXp,
                  speedBonusXp: participant.speedBonusXp,
                  pvpAppXpBonusAmount: participant.pvpAppXpBonusAmount,
                  pvpAppXpBonusPercent: participant.pvpAppXpBonusPercent,
                  soloXpPotionBonusAmount: participant.soloXpPotionBonusAmount,
                  soloXpPotionBonusPercent: participant.soloXpPotionBonusPercent,
                }}
              />
            )}
          </div>
        ))}
      </div>

      {anyXp && !isPwaStandalone() && !anyPvpAppBonus && (
        <Alert
          variant="info"
          className="mt-3 mb-0 text-center quiz-results-install-app-alert d-flex flex-column align-items-center"
        >
          <div className="mb-2">{t('quiz.installAppXpTeaser', 'Earn 15% more XP by playing in the app.')}</div>
          <PwaInstallButton />
        </Alert>
      )}
    </div>
  );
};

export default PvpQuizResultsModalContent;
